import "./address-book-pickup-delivery-card.css";

import { Label } from "@tql/ui-components-react/kendo/labels";
import { Popover } from "@tql/ui-components-react/kendo/tooltip";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { Checkbox } from "@tql/ui-components-react/tql/inputs/beta";
import { infoCircle } from "@tql/ui-components-react/untitled/icons";
import { useRef, useState } from "react";

import { LocationTypeOptions } from "~/features/quote/components/quote-details/components/pickup-delivery-details/components/location-type-options/location-type-options.tsx";
import { ServiceOptions } from "~/features/quote/components/quote-details/components/pickup-delivery-details/components/service-options/service-options.tsx";
import {
	APPOINTMENT_SERVICE_NAME,
	RESIDENTIAL_SERVICE_NAME,
} from "~/features/quote/components/quote-details/components/pickup-delivery-details/hooks/use-pickup-delivery-details.ts";
import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { AddressBookContactDetail } from "~/shared/schemas/address-book-schema.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types";

import { capitalizeWord } from "../../../../../../shared/utils/string-fns.ts";

type AddressBookPickupDeliveryCardProps = {
	type: SubGroupKey;
	detail?: AddressBookContactDetail;
	fieldPrefix: string;
	value?: Accessorial[];
	onChange?: (value: Accessorial[]) => void;
	isDefault?: boolean;
	onDefaultChange?: (checked: boolean) => void;
	isDefaultDisabled?: boolean;
	locationTypeOptions: Accessorial[];
	serviceOptions: Accessorial[];
	disabled?: boolean;
	hideDefault?: boolean;
};

export const AddressBookPickupDeliveryCard = ({
	type,
	fieldPrefix,
	value = [],
	onChange,
	isDefault = false,
	onDefaultChange,
	isDefaultDisabled = false,
	locationTypeOptions,
	serviceOptions,
	disabled = false,
	hideDefault = false,
}: AddressBookPickupDeliveryCardProps) => {
	const anchor = useRef<HTMLDivElement>(null);
	const [showBolPopover, setShowBolPopover] = useState(false);

	const toggleBolPopover = () => {
		setShowBolPopover(!showBolPopover);
	};

	const handleRadioSelectionChange = (option: Accessorial) => {
		const otherAccessorials = value.filter(
			(a) => !locationTypeOptions.some((l) => l.id === a.id),
		);

		let newValue = [...otherAccessorials, option];

		if (type === "delivery") {
			if (option.name === RESIDENTIAL_SERVICE_NAME) {
				const appointmentOption = serviceOptions.find(
					(s) => s.name === APPOINTMENT_SERVICE_NAME,
				);
				if (
					appointmentOption &&
					!newValue.some((a) => a.id === appointmentOption.id)
				) {
					newValue = [...newValue, appointmentOption];
				}
			} else {
				newValue = newValue.filter((a) => a.name !== APPOINTMENT_SERVICE_NAME);
			}
		}

		onChange?.(newValue);
	};

	const isOptionSelected = (option: Accessorial): boolean => {
		return value.some((a) => a.id === option.id);
	};

	const handleCombinedSelectionChange = (
		option: Accessorial,
		checked: boolean,
	) => {
		if (
			type === "delivery" &&
			option.name === APPOINTMENT_SERVICE_NAME &&
			!checked
		) {
			const isResidentialSelected = value.some(
				(a) =>
					a.name === RESIDENTIAL_SERVICE_NAME &&
					locationTypeOptions.some((l) => l.id === a.id),
			);
			if (isResidentialSelected) {
				return;
			}
		}

		if (checked) {
			if (!value.some((a) => a.id === option.id)) {
				onChange?.([...value, option]);
			}
			return;
		}
		onChange?.(value.filter((p) => p.id !== option.id));
	};

	return (
		<div
			className={"address-book-pickup-section address-book-section"}
			data-testid={`address-book-${type}-details-section`}
		>
			<div className={"address-book-section-header"}>
				<div className={"address-book-section-header-title"}>
					{capitalizeWord(type)}
				</div>
				{hideDefault ? null : (
					<>
						<Checkbox
							checked={isDefault}
							disabled={disabled || isDefaultDisabled}
							id={`address-book-${type}-default`}
							onChange={(e) => onDefaultChange?.(e.value)}
						/>
						<Label editorId={`address-book-${type}-default`}>Default</Label>
					</>
				)}
			</div>
			<div className={"address-book-pickup-delivery-content-wrapper"}>
				<div className={"address-book-accessorials-wrapper"}>
					<b>Accessorials:</b>
					<LocationTypeOptions
						disabled={disabled}
						handleRadioSelectionChange={handleRadioSelectionChange}
						isOptionSelected={isOptionSelected}
						locationTypeOptions={locationTypeOptions}
						name={type}
					/>
					<ServiceOptions
						disabled={disabled}
						handleCombinedSelectionChange={handleCombinedSelectionChange}
						isOptionSelected={isOptionSelected}
						locationTypeOptions={locationTypeOptions}
						name={type}
						serviceOptions={serviceOptions}
					/>
				</div>

				<div className={"address-book-flex-inputs"}>
					<ValidationInputField
						disabled={disabled}
						fieldType={FieldType.TIMEPICKER}
						id={`${fieldPrefix}_hoursOpen`}
						label={"Hours Open"}
						name={`${fieldPrefix}_hoursOpen`}
						placeholder={""}
						wrapperStyle={{ width: "120px" }}
					/>
					<ValidationInputField
						disabled={disabled}
						fieldType={FieldType.TIMEPICKER}
						id={`${fieldPrefix}_hoursClose`}
						label={"Hours Close"}
						name={`${fieldPrefix}_hoursClose`}
						placeholder={""}
						wrapperStyle={{ width: "120px" }}
					/>
					<ValidationInputField
						disabled={disabled}
						fieldType={FieldType.INPUT}
						id={`${fieldPrefix}_contactName`}
						label={"Contact Name"}
						maxLength={50}
						name={`${fieldPrefix}_contactName`}
						wrapperStyle={{ flex: 1 }}
					/>
					<ValidationInputField
						disabled={disabled}
						fieldType={FieldType.MASKEDTEXTBOX}
						id={`${fieldPrefix}_contactPhone`}
						label={"Phone"}
						mask={"(000) 000-0000"}
						name={`${fieldPrefix}_contactPhone`}
						placeholder={"(___) ___-____"}
						wrapperStyle={{ width: "115px" }}
					/>
					<ValidationInputField
						disabled={disabled}
						fieldType={FieldType.INPUT}
						id={`${fieldPrefix}_contactExtension`}
						inputMode={"numeric"}
						label={"Ext"}
						maxLength={10}
						name={`${fieldPrefix}_contactExtension`}
						pattern={"[0-9]*"}
						wrapperStyle={{ width: "85px" }}
					/>
				</div>
				<div className={"address-book-special-instructions-wrapper"}>
					<div className={"label-wrapper"}>
						<Label editorId={`${fieldPrefix}_specialInstructions`}>
							BOL Special Instructions
						</Label>
						<div ref={anchor}>
							<Button
								aria-label={`${type} BOL special instructions info`}
								data-testid={`address-book-${type}-bol-info-button`}
								formAction={"none"}
								onClick={toggleBolPopover}
								svgIcon={infoCircle}
								variant={"tertiary"}
							/>
						</div>
						<Popover
							anchor={anchor.current}
							animate={false}
							contentStyle={{ maxWidth: 350 }}
							popoverClass={"tql-popover"}
							position={"top"}
							show={showBolPopover}
						>
							The text entered will appear under “Special Instructions” on the
							Bill of Lading (BOL) whenever this address is used as the {type}{" "}
							location.
						</Popover>
					</div>

					<ValidationInputField
						disabled={disabled}
						fieldType={FieldType.TEXTAREA}
						id={`${fieldPrefix}_specialInstructions`}
						maxLength={500}
						name={`${fieldPrefix}_specialInstructions`}
					/>
				</div>
			</div>
		</div>
	);
};
